// File: src/components/MyAccountView.js
// What a competitor sees when they sign in: their own details, and the races
// they have entered.
//
// Staff have the admin panel; a competitor has this. Everything here is scoped
// to the signed-in person by the API, which takes the id from the token.

import React, { useState, useEffect, useCallback } from 'react';
import { Trophy, LogOut, Key, User, Calendar, MapPin, Radio, Plus, Trash2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { formatDateForDisplay } from '../utils/utils';
import { apiGet, apiPost, apiPut, apiDelete } from '../services/api';
import ThemeToggle from './ThemeToggle';
import { LanguageSelect, HEADER_BUTTON, HEADER_BUTTON_DANGER } from './HeaderControls';
import CountryOptions from './CountryOptions';

const STATUS_CLASSES = {
  registered: 'bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400',
  waitlisted: 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-800 dark:text-yellow-200',
  cancelled: 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400'
};

const EDITABLE = ['first_name', 'last_name', 'email', 'phone', 'club', 'birth_year', 'country'];

function MyAccountView({ token, username, handleLogout, setShowPasswordChange, onBrowseCalendar }) {
  const { t } = useLanguage();
  const [form, setForm] = useState({});
  // The saved transponder list. It mostly fills itself as races are entered;
  // this panel is for labelling and pruning it.
  const [transponders, setTransponders] = useState([]);
  const [newTransponder, setNewTransponder] = useState({ number: '', label: '' });
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [me, myEntries] = await Promise.all([
        apiGet('/me/profile', { token }),
        apiGet('/me/entries', { token })
      ]);
      setForm(Object.fromEntries(EDITABLE.map(f => [f, me[f] ?? ''])));
      setTransponders(me.transponders || []);
      setEntries(myEntries);
      setError('');
    } catch (err) {
      setError((err.body && err.body.message) || err.message || t('accountLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [token, t]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    setMessage('');
    setError('');
    try {
      // The server returns the saved row; re-seed the form from it so any
      // trimming or normalising it did is visible.
      // The whole form goes up, so an unanswered country would be sent as an
      // empty string and refused. Fall back to what the select is showing.
      const updated = await apiPut('/me/profile',
        { ...form, country: form.country || 'FI' }, { token });
      setForm(Object.fromEntries(EDITABLE.map(f => [f, updated[f] ?? ''])));
      setTransponders(updated.transponders || []);
      setMessage(t('accountSaved'));
    } catch (err) {
      setError((err.body && err.body.message) || err.message || t('accountSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const field = (name, label, type = 'text') => (
    <div>
      <label className="block text-ink-muted mb-2 font-semibold">{label}</label>
      <input
        type={type}
        value={form[name] ?? ''}
        onChange={(e) => setForm({ ...form, [name]: e.target.value })}
        className="w-full px-4 py-2 bg-paper-alt border border-line rounded text-ink"
      />
    </div>
  );

  // The one profile field that is a choice rather than free text.
  const countryField = (
    <div>
      <label className="block text-ink-muted mb-2 font-semibold" htmlFor="profile-country">
        {t('country')}
      </label>
      <select
        id="profile-country"
        value={form.country || 'FI'}
        onChange={(e) => setForm({ ...form, country: e.target.value })}
        className="w-full px-4 py-2 bg-paper-alt border border-line rounded text-ink"
      >
        <CountryOptions />
      </select>
    </div>
  );

  const addTransponder = async () => {
    if (!newTransponder.number.trim()) return;
    setMessage('');
    setError('');
    try {
      const created = await apiPost('/me/transponders', newTransponder, { token });
      setTransponders([...transponders, created]);
      setNewTransponder({ number: '', label: '' });
    } catch (err) {
      setError((err.body && err.body.message) || err.message || t('accountSaveFailed'));
    }
  };

  const removeTransponder = async (id) => {
    setMessage('');
    setError('');
    try {
      await apiDelete(`/me/transponders/${id}`, { token });
      // Entries already made keep their number; this only changes what is
      // offered next time.
      setTransponders(transponders.filter(item => item.id !== id));
    } catch (err) {
      setError((err.body && err.body.message) || err.message || t('accountSaveFailed'));
    }
  };

  const statusLabel = (status) => ({
    registered: t('entryStatusRegistered'),
    waitlisted: t('entryStatusWaitlisted'),
    cancelled: t('entryStatusCancelled')
  }[status] || status);

  return (
    <div className="app-shell p-4">
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-wrap justify-between items-center gap-3 mb-8">
          <div className="flex items-center gap-3">
            <Trophy className="w-10 h-10 text-accent" />
            <div>
              <h1 className="text-3xl font-bold text-ink">{t('accountTitle')}</h1>
              <p className="text-ink-muted text-sm">
                {t('accountUsername')}: {username} · {t('competitor')}
              </p>
            </div>
          </div>

          {/* The last hamburger, holding four items. Same treatment as the
              public and admin headers. */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={onBrowseCalendar}
              className={HEADER_BUTTON}
              title={t('accountBrowseCalendar')}
              aria-label={t('accountBrowseCalendar')}
            >
              <Calendar className="w-4 h-4" />
              <span className="hidden sm:inline">{t('accountBrowseCalendar')}</span>
            </button>

            <button
              onClick={() => setShowPasswordChange(true)}
              className={HEADER_BUTTON}
              title={t('changePassword')}
              aria-label={t('changePassword')}
            >
              <Key className="w-4 h-4" />
            </button>

            <LanguageSelect />

            <button
              onClick={handleLogout}
              className={HEADER_BUTTON_DANGER}
              title={t('logout')}
              aria-label={t('logout')}
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">{t('logout')}</span>
            </button>

            <ThemeToggle />
          </div>
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-500/20 border border-red-500 text-red-800 dark:text-red-200 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {loading ? (
          <p className="text-ink-muted">{t('loading')}</p>
        ) : (
          <div className="space-y-6">
            <div className="bg-paper border border-line rounded-lg p-6">
              <h2 className="text-xl font-bold text-ink mb-1 flex items-center gap-2">
                <User className="w-5 h-5 text-accent" />
                {t('accountProfile')}
              </h2>
              <p className="text-ink-muted text-sm mb-4">{t('accountPrefillHelp')}</p>

              {message && (
                <div className="bg-green-50 dark:bg-green-500/20 border border-green-500 text-green-800 dark:text-green-200 px-4 py-3 rounded mb-4">
                  {message}
                </div>
              )}

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {field('first_name', t('entryFirstName'))}
                  {field('last_name', t('entryLastName'))}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {field('email', t('entryEmail'), 'email')}
                  {field('phone', t('entryPhone'))}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {field('club', t('entryClub'))}
                  {field('birth_year', t('entryBirthYear'), 'number')}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {countryField}
                </div>
              </div>

              <button
                onClick={save}
                disabled={saving}
                className="mt-6 bg-accent hover:opacity-90 disabled:opacity-50 text-accent-ink px-6 py-2 rounded font-semibold"
              >
                {t('save')}
              </button>
            </div>

            <div className="bg-paper border border-line rounded-lg p-6">
              <h2 className="text-xl font-bold text-ink mb-2 flex items-center gap-2">
                <Radio className="w-5 h-5 text-accent" />
                {t('myTransponders')}
              </h2>
              <p className="text-ink-muted text-sm mb-4">{t('myTranspondersHint')}</p>

              {transponders.length === 0 ? (
                <p className="text-ink-muted mb-4">{t('transponderNoneSaved')}</p>
              ) : (
                <div className="space-y-2 mb-4">
                  {transponders.map(item => (
                    <div key={item.id} className="bg-paper-alt rounded px-3 py-2 flex items-center gap-3">
                      <span className="text-ink font-mono-num">{item.number}</span>
                      <span className="text-ink-muted text-sm flex-1 truncate">{item.label || ''}</span>
                      <button
                        onClick={() => removeTransponder(item.id)}
                        className="text-red-700 dark:text-red-400 hover:opacity-80"
                        title={t('transponderRemove')}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[10rem]">
                  <label className="block text-ink-muted mb-2 font-semibold text-sm">
                    {t('entryTransponderNumber')}
                  </label>
                  <input
                    type="text"
                    value={newTransponder.number}
                    onChange={(e) => setNewTransponder({ ...newTransponder, number: e.target.value })}
                    className="w-full px-4 py-2 bg-paper-alt border border-line rounded text-ink font-mono-num"
                  />
                </div>
                <div className="flex-1 min-w-[10rem]">
                  <label className="block text-ink-muted mb-2 font-semibold text-sm">
                    {t('transponderLabelField')}
                  </label>
                  <input
                    type="text"
                    value={newTransponder.label}
                    onChange={(e) => setNewTransponder({ ...newTransponder, label: e.target.value })}
                    className="w-full px-4 py-2 bg-paper-alt border border-line rounded text-ink"
                  />
                </div>
                <button
                  onClick={addTransponder}
                  className="bg-paper-alt hover:opacity-80 text-ink px-4 py-2 rounded font-semibold flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  {t('transponderAdd')}
                </button>
              </div>
            </div>

            <div className="bg-paper border border-line rounded-lg p-6">
              <h2 className="text-xl font-bold text-ink mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-accent" />
                {t('accountMyEntries')}
              </h2>

              {entries.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-ink-muted mb-4">{t('accountNoEntries')}</p>
                  <button
                    onClick={onBrowseCalendar}
                    className="bg-paper-alt hover:opacity-80 text-ink px-6 py-2 rounded font-semibold"
                  >
                    {t('accountBrowseCalendar')}
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {entries.map(entry => (
                    <div key={entry.id} className="bg-paper-alt rounded p-4 flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-ink font-semibold">{entry.event_name}</div>
                        <div className="text-ink-muted text-sm mt-1 flex items-center gap-2 flex-wrap">
                          <Calendar className="w-4 h-4" />
                          {formatDateForDisplay(entry.event_start_date)}
                          {entry.event_end_date && ` – ${formatDateForDisplay(entry.event_end_date)}`}
                          {(entry.venue || entry.city) && (
                            <>
                              <MapPin className="w-4 h-4 ml-2" />
                              {[entry.venue, entry.city].filter(Boolean).join(', ')}
                            </>
                          )}
                        </div>
                        {entry.classes.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {entry.classes.map(cls => (
                              <span key={cls.id} className="text-xs px-2 py-1 rounded bg-paper text-ink-muted">
                                {cls.name}
                                {cls.transponder_number && (
                                  <span className="font-mono-num"> · {cls.transponder_number}</span>
                                )}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-3">
                        {/* Editing an account entry means the organizer's list;
                            there is no manage token to follow. */}
                        {!entry.can_still_edit && (
                          <span className="text-ink-muted text-xs">{t('accountEntryClosed')}</span>
                        )}
                        <span className={`text-xs font-semibold px-2 py-1 rounded whitespace-nowrap ${STATUS_CLASSES[entry.status] || ''}`}>
                          {statusLabel(entry.status)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default MyAccountView;
